// eslint-disable-next-line max-classes-per-file
import { isArray } from 'lodash'
import i18next from 'i18next'
import { Tree, TreeNode } from '../../../pages/reporting/custom/components/customReportTable/Tree'
import { Dimension, DimensionQuery, DimensionQueryOperator } from '../types/Dimension'

export class DimensionTreeNode extends TreeNode<Dimension> {
  constructor(key: string, value = null as Dimension | null, parent = null as DimensionTreeNode['parent']) {
    super(key, value, parent)
  }
}

export class DimensionTree extends Tree<Dimension> {
  root: DimensionTreeNode

  constructor(key = 'root') {
    super(key)
    this.root = new DimensionTreeNode(key)
  }

  static queryBuilder(ids: string[][] | string[]) {
    if (!ids || ids.length === 0) return undefined

    const recurse = (val: string[][] | string[] | string): DimensionQuery => {
      if (isArray(val)) {
        if (val.length > 1) {
          const operator = isArray(val[0]) ? DimensionQueryOperator.and : DimensionQueryOperator.or
          return {
            o: operator,
            c: val.map(child => recurse(child))
          }
        }
        return recurse(val[0])
      }
      return {
        o: DimensionQueryOperator.has,
        v: val
      }
    }

    const query = recurse(ids)
    return query
  }

  insert(parentNodeKey: string, key: string, value = null as Dimension | null) {
    const node = this.find(parentNodeKey)
    if (node?.key === parentNodeKey) {
      node.children.push(new DimensionTreeNode(key, value, node))
      return true
    }
    return false
  }

  reset() {
    this.root.children.forEach(c => this.remove(c.key))
  }

  build(list: Dimension[]) {
    // Pre-build lookup maps for O(1) access
    const dimensionMap = new Map(list.map(d => [d.dimensionId, d]))
    const childrenMap = new Map<string | null, Dimension[]>()

    // Group dimensions by their parent - O(n) operation
    for (const dimension of list) {
      const parentId = dimension.parentDimensionId
      if (!childrenMap.has(parentId as string)) {
        childrenMap.set(parentId as string, [])
      }
      childrenMap.get(parentId as string)!.push(dimension)
    }

    // Now build the tree in O(n) time
    for (const node of this.preOrderTraversal()) {
      const parentId = node.key === 'root' ? null : node.key

      // Get direct children for this parent
      let children = childrenMap.get(parentId) || []

      // For root node, also include orphaned dimensions
      if (node.key === 'root') {
        const orphaned = childrenMap.get(null) || []
        const orphanedByMissingParent = []

        // Find dimensions whose parents don't exist
        for (const [parentIdd, dims] of childrenMap.entries()) {
          if (parentIdd !== null && !dimensionMap.has(parentIdd)) {
            orphanedByMissingParent.push(...dims)
          }
        }

        children = [...orphaned, ...orphanedByMissingParent]
      }

      // Convert to tree nodes
      const treeNodes = children.map(c => new DimensionTreeNode(c.dimensionId, c, node))
      node.children.push(...treeNodes)

      // Remove processed children from the map to avoid duplicates
      if (node.key === 'root') {
        childrenMap.delete(null)
        // Remove orphaned entries
        for (const [parentIdd] of childrenMap.entries()) {
          if (parentIdd !== null && !dimensionMap.has(parentIdd)) {
            childrenMap.delete(parentIdd)
          }
        }
      } else {
        childrenMap.delete(parentId)
      }
    }
  }

  getQuery(ids?: string[]): string | undefined {
    const subTrees = this.getSubTrees(ids)

    if (!subTrees || !ids || ids.length === 0) return undefined

    const query = DimensionTree.queryBuilder(subTrees)

    return JSON.stringify([query])
  }

  getSubTrees(ids?: string[], propertyName: keyof Dimension = 'dimensionId') {
    if (!ids || ids.length === 0) return undefined
    const subTrees: string[][] = []

    this.root.children.forEach(rootNode => {
      const subTreeIds: string[] = []
      for (const node of this.preOrderTraversal(rootNode)) {
        if (ids.includes(`${node?.key}`)) {
          subTreeIds.push(node?.value?.[propertyName] as string)
        }
      }
      if (subTreeIds.length > 0) {
        subTrees.push(subTreeIds)
      }
    })
    return subTrees
  }

  getDimensionText(ids?: string[]) {
    const subTrees = this.getSubTrees(ids, 'name')

    if (!subTrees || !ids || ids.length === 0) return null

    const a = subTrees.map(sub => sub.join(` ${i18next.t('global:or')} `)).join(` ${i18next.t('global:and')} `)

    return a
  }

  getNamedSubTrees(ids?: string[]) {
    const subTrees = this.getSubTrees(ids, 'name')

    return subTrees
  }
}
